import React, { useLayoutEffect, useRef } from 'react';
import { Box, Presence } from '@chakra-ui/react';
import { keyframes } from '@emotion/react';
import { EASE_OUT } from '@/components/ui/motion';

interface CompletionSwapProps {
	pending: boolean;
	pendingContent: React.ReactNode;
	children: React.ReactNode;
}

const fadeIn = keyframes`
	from { opacity: 0; }
	to { opacity: 1; }
`;

const fadeOut = keyframes`
	from { opacity: 1; }
	to { opacity: 0; }
`;

const presenceCss = {
	'&[data-state=open]': { animation: `${fadeIn} 160ms ${EASE_OUT} both` },
	'&[data-state=closed]': { animation: `${fadeOut} 120ms ${EASE_OUT} both` },
	'@media (prefers-reduced-motion: reduce)': {
		'&[data-state=open]': { animation: `${fadeIn} 120ms ${EASE_OUT} both` },
		'&[data-state=closed]': { animation: `${fadeOut} 100ms ${EASE_OUT} both` },
	},
} as const;

/** Crossfades generated-output or status content without animating the surrounding layout height. */
const CompletionSwap: React.FC<CompletionSwapProps> = ({ pending, pendingContent, children }) => {
	const lastPendingContent = useRef(pendingContent);
	useLayoutEffect(() => {
		if (pending) lastPendingContent.current = pendingContent;
	}, [pending, pendingContent]);
	const renderedPendingContent = pending ? pendingContent : lastPendingContent.current;

	return (
		<Box display="grid" minW={0} maxW="full" alignItems="center">
			<Presence present={pending} unmountOnExit skipAnimationOnMount gridArea="1 / 1" minW={0} css={presenceCss}>
				{renderedPendingContent}
			</Presence>
			<Presence present={!pending} unmountOnExit skipAnimationOnMount gridArea="1 / 1" minW={0} css={presenceCss}>
				{children}
			</Presence>
		</Box>
	);
};

export default CompletionSwap;
